import api from "../apiClient";

interface LockMarker {
  t: number;
  facilityId: string;
  employeeEmail: string;
}

const LOCK_MARKER_KEY = "ls.lastLockAt";

// Deliberately duplicated from sessionLock.ts's own HARD_SIGNOUT_MS rather
// than imported (review fix, cycle 3): this module is statically imported
// by useAuth.ts, which loads on every platform, while sessionLock.ts must
// stay reachable only via NativeSessionGuard's dynamic `import()` under
// isCapacitor (US-015 AC11 — not modulepreloaded on web/desktop). A static
// import here pulled that whole chunk into every platform's bundle graph
// with zero functional difference, only dead-code cost. sessionLock.test.ts
// already duplicates this same literal for the identical reason — keep the
// two values in sync by hand if the ceiling ever changes.
const HARD_SIGNOUT_MS = 28_800_000; // 8 h — matches sessionLock.ts

/**
 * US-176 Phase 0 review fix (Critical) — the counterpart to
 * NativeSessionGuard's reportLockEvent(). employeeEmail binds the marker to
 * whoever locked the device: on a SHARED cart, employee A's shift can end
 * right after a lock with no resume, leaving the marker to sit until
 * employee B's next cold sign-in. Without this check B's sign-in would
 * silently consume A's marker and report an hours-long duration under B's
 * employeeId — corrupting the exact metric this instrumentation exists to
 * produce, for the exact scenario the story exists to measure. Compared
 * case/whitespace-insensitively — the marker is written from the canonical
 * `AuthContext.email`, but the compare side is the raw string typed into
 * the login form, and this codebase normalizes email comparisons elsewhere
 * (EmployeesPage.tsx, InviteAuditors.tsx) for exactly this reason.
 *
 * Cycle-2 review fix (Critical, round 2) — email matching alone doesn't
 * bound STALENESS: the SAME employee returning to the SAME shared cart
 * days later (a small rotating staff roster on a limited set of carts is
 * this story's own deployment model, §1) would otherwise still report a
 * multi-day "resume" duration, corrupting the metric via a different path
 * than the cross-employee case. A marker older than HARD_SIGNOUT_MS is, by
 * this codebase's own definition (sessionLock.ts's "Ceiling first" comment
 * — crossing the 8h ceiling ends the session as a hard sign-out, "a fresh
 * cold sign-in follows, not a resumed lock"), never a genuine resume.
 *
 * Called from useAuth.ts's handleNextStep "DONE" branch — the single point
 * every sign-in completion path converges on (password-only, password+new-
 * password, password+MFA all route through it), which is also where the
 * email of whoever just signed in is synchronously available. If a lock
 * marker exists, belongs to this same email, AND is younger than the hard
 * ceiling, this was a genuine resume-from-lock; report the elapsed
 * duration and clear the marker. Any other case (no marker, a different
 * employee's marker, or a stale one) clears the marker without reporting.
 *
 * Fire-and-forget: reporting must never affect the sign-in flow itself.
 */
export function reportReauthCompletionIfLocked(
  currentEmail: string | null,
): void {
  const raw = localStorage.getItem(LOCK_MARKER_KEY);
  if (!raw) return;
  localStorage.removeItem(LOCK_MARKER_KEY);

  let marker: LockMarker;
  try {
    marker = JSON.parse(raw) as LockMarker;
    if (
      typeof marker.t !== "number" ||
      typeof marker.facilityId !== "string" ||
      typeof marker.employeeEmail !== "string"
    ) {
      return;
    }
  } catch {
    return;
  }

  const normalizedCurrent = currentEmail?.trim().toLowerCase() ?? null;
  const normalizedMarker = marker.employeeEmail.trim().toLowerCase();
  if (!normalizedCurrent || normalizedMarker !== normalizedCurrent) return;

  // A backwards clock jump (NTP correction, manual change) would otherwise
  // make elapsed time negative — same defense as sessionLock.ts's idle
  // check, applied here since the backend rejects a negative duration.
  const elapsedMs = Date.now() - marker.t;
  if (elapsedMs >= HARD_SIGNOUT_MS) return; // stale — not a genuine resume
  const reauthDurationMs = Math.max(0, elapsedMs);

  // US-176 Phase 0 (simplified 2026-09-12): report the ORIGINAL lock event
  // here too, not just the reauth-completion duration. NativeSessionGuard
  // deliberately does not attempt this at lock time — see its own comment
  // for why that moment is structurally hostile to a network call (races
  // logoff()'s navigation, or blocks it if awaited). This call site has
  // neither problem: it runs after handleNextStep's plain in-SPA state
  // update, with no document navigation involved, so a normal
  // fire-and-forget POST here actually survives. client_event_at uses the
  // marker's own timestamp (when the lock actually happened), not now —
  // the WRITE is delayed until the next sign-in, but the recorded time
  // is not, so the kill-criterion's per-shift frequency is unaffected by
  // the delay.
  void api
    .post("/analytics/session-lock-events", {
      facility_id: marker.facilityId,
      event_type: "lock",
      client_event_at: new Date(marker.t).toISOString(),
    })
    .catch(() => {});

  void api
    .post("/analytics/session-lock-events", {
      facility_id: marker.facilityId,
      event_type: "reauth_complete",
      reauth_duration_ms: reauthDurationMs,
      client_event_at: new Date().toISOString(),
    })
    .catch(() => {});
}
